import { getRunStepDisplay, runStepStatusNote } from "./runStepDisplay";
import { getExecutionReport } from "./executionReport";

const COMPLETE_STATUSES = new Set(["done", "warning", "skipped"]);

export const MISSION_CONTROL_STAGES = [
  {
    id: "intent",
    label: "Intent Locked",
    shortLabel: "Intent Locked",
    phaseIds: ["collect_inputs", "interpret_intent"],
    match: (text) => text.includes("analyz") || text.includes("interpret") || text.includes("selected skills"),
    completeDetail: "User request, audience, constraints, and format instructions were interpreted.",
  },
  {
    id: "research",
    label: "Research & Sources",
    shortLabel: "Research & Sources",
    phaseIds: ["retrieve_sources", "digest_sources"],
    match: (text) => text.includes("research") || text.includes("searched") || text.includes("source") || text.includes("digest"),
    completeDetail: "Research inputs were collected and turned into usable source notes.",
  },
  {
    id: "plan",
    label: "Content Plan",
    shortLabel: "Content Plan",
    phaseIds: ["build_plan"],
    match: (text) => text.includes("content plan") || text.includes("strategy") || text.includes("created strategy"),
    completeDetail: "The drafting plan was built from the prompt, platform, brand, skills, and constraints.",
  },
  {
    id: "draft",
    label: "Draft & Repair",
    shortLabel: "Draft & Repair",
    phaseIds: ["draft_posts", "critique_repair", "normalize_output"],
    match: (text) => text.includes("critique") || text.includes("revis") || text.includes("format") || text.includes("validated") || text.includes("normalize") || text.includes("repair"),
    completeDetail: "Draft copy was generated, checked, repaired, and normalized for platform review.",
  },
  {
    id: "visual",
    label: "Visual Direction",
    shortLabel: "Visual Direction",
    phaseIds: ["generate_image_prompt"],
    match: (text) => text.includes("image") || text.includes("visual") || text.includes("cover"),
    completeDetail: "A supporting visual direction and image prompt were prepared.",
  },
  {
    id: "review",
    label: "Ready for Review",
    shortLabel: "Ready for Review",
    phaseIds: ["ready_for_review"],
    match: (text) => text.includes("ready for review") || text.includes("review"),
    completeDetail: "Drafts and supporting materials are packaged for human review.",
  },
];

function compactText(value = "", limit = 90) {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  if (text.length <= limit) return text;
  return `${text.slice(0, Math.max(0, limit - 3)).trimEnd()}...`;
}

function normalizeResearchMode(mode = "") {
  return String(mode || "none").trim().toLowerCase().replace(/[-\s]+/g, "_");
}

function pluralize(count, singular, plural = `${singular}s`) {
  return `${count} ${count === 1 ? singular : plural}`;
}

function getStageDefinitionForStep(step = {}) {
  const phaseId = step.workflow_phase_id || "";
  const text = `${step.label || ""} ${step.phase || ""}`.toLowerCase();
  return MISSION_CONTROL_STAGES.find((stage) => stage.phaseIds.includes(phaseId))
    || MISSION_CONTROL_STAGES.find((stage) => stage.match(text))
    || MISSION_CONTROL_STAGES.find((stage) => stage.id === "draft");
}

function getGroupedStatus(stage, steps, runStatus, researchMode, counts) {
  if (!steps.length) {
    if (stage.id === "research" && ["none", "off", "no_research"].includes(researchMode)) return "skipped";
    if (runStatus === "completed") {
      if (stage.id === "research") return "skipped";
      if (stage.id === "visual") return counts.supportingCount ? "done" : "skipped";
      return "done";
    }
    return "pending";
  }
  if (steps.some((step) => step.status === "failed" || step.status === "error")) return "failed";
  if (steps.some((step) => step.status === "running")) return "running";
  if (steps.some((step) => step.status === "warning")) return "warning";
  if (steps.every((step) => COMPLETE_STATUSES.has(step.status))) return "done";
  return "pending";
}

function getMaxSourceCount(steps) {
  return steps.reduce((max, step) => {
    const count = Number(step.source_count);
    return Number.isFinite(count) ? Math.max(max, count) : max;
  }, 0);
}

function getStageQuery(steps) {
  const queryStep = steps.find((step) => step.query);
  return compactText(queryStep?.query || "", 160);
}

function getResearchDetail(steps, status, researchMode) {
  if (status === "skipped" || ["none", "off", "no_research"].includes(researchMode)) return "Research was off for this run.";
  const queryStep = steps.find((step) => step.query || getRunStepDisplay(step).note);
  const sourceCount = getMaxSourceCount(steps);
  const query = queryStep?.query || "";
  if (query && sourceCount) return `Query: ${compactText(query, 120)} - ${pluralize(sourceCount, "source")}`;
  if (query) return `Query: ${compactText(query, 120)}`;
  if (sourceCount) return `${pluralize(sourceCount, "source")} reviewed before drafting.`;
  return "Research inputs were collected and turned into usable source notes.";
}

function getMetric(stageId, steps, counts, status, researchMode) {
  if (stageId === "intent") return status === "done" ? "Locked" : steps.length ? pluralize(steps.length, "phase") : "Waiting";
  if (stageId === "research") {
    const sourceCount = getMaxSourceCount(steps);
    if (sourceCount) return pluralize(sourceCount, "source");
    if (status === "skipped" || ["none", "off", "no_research"].includes(researchMode)) return "Off";
    return "Sources";
  }
  if (stageId === "plan") return status === "done" ? "Plan locked" : steps.length ? pluralize(steps.length, "phase") : "Waiting";
  if (stageId === "draft") return pluralize(counts.deliverableCount || 0, "post");
  if (stageId === "visual") return pluralize(counts.supportingCount || 0, "material");
  if (stageId === "review") return status === "done" ? "Review" : "Gate";
  return steps.length ? pluralize(steps.length, "phase") : "Waiting";
}

function getStageDetail(stage, steps, status, researchMode) {
  const problemStep = steps.find((step) => ["failed", "error", "warning"].includes(step.status));
  if (problemStep) return problemStep.note || problemStep.error || getRunStepDisplay(problemStep).note || runStepStatusNote(problemStep.status);
  const runningStep = steps.find((step) => step.status === "running");
  if (runningStep) {
    const display = getRunStepDisplay(runningStep);
    return display.note ? `Working now: ${display.note}` : `Working now: ${display.label}.`;
  }
  if (stage.id === "research") return getResearchDetail(steps, status, researchMode);
  if (status === "skipped") return `${stage.label} was not needed for this run.`;
  return stage.completeDetail;
}

function toExactPhase(step = {}, index) {
  const display = getRunStepDisplay(step);
  return {
    id: step.id || step.workflow_phase_id || `${index}`,
    label: compactText(display.label || step.label || "Agent phase"),
    status: step.status || "pending",
    note: display.note || step.note || runStepStatusNote(step.status),
    duration_ms: step.duration_ms,
    workflow_phase_id: step.workflow_phase_id || "",
  };
}

export function buildMissionControlStages(run = {}, counts = {}) {
  const researchMode = normalizeResearchMode(run.research_mode);
  const grouped = MISSION_CONTROL_STAGES.reduce((acc, stage) => ({ ...acc, [stage.id]: [] }), {});
  const steps = Array.isArray(run.steps) ? run.steps : [];

  steps.forEach((step) => {
    const stage = getStageDefinitionForStep(step);
    grouped[stage.id].push(step);
  });

  return MISSION_CONTROL_STAGES.map((stage) => {
    const stageSteps = grouped[stage.id] || [];
    const status = getGroupedStatus(stage, stageSteps, run.status, researchMode, counts);
    return {
      ...stage,
      status,
      metric: getMetric(stage.id, stageSteps, counts, status, researchMode),
      sourceCount: getMaxSourceCount(stageSteps),
      query: getStageQuery(stageSteps),
      detail: getStageDetail(stage, stageSteps, status, researchMode),
      exactPhases: stageSteps.map(toExactPhase),
    };
  });
}

/* -------------------------------------------------------------------------- */
/*  Real per-phase OUTPUT extraction (populates the run feed's phases with the */
/*  agent's actual work: sources it found, the query it wrote, the image       */
/*  prompt it made, the prompt/plan it acted on).                              */
/* -------------------------------------------------------------------------- */

function domainFromUrl(value = "") {
  const text = String(value || "").trim();
  if (!text) return "";
  try { return new URL(text).hostname.replace(/^www\./, ""); }
  catch (_) { return text.replace(/^https?:\/\//, "").replace(/^www\./, "").split(/[/?#]/)[0]; }
}

export function confidenceLabel(value = "") {
  const text = String(value || "").trim().toLowerCase();
  if (text.includes("low")) return "Low";
  if (text.includes("medium")) return "Medium";
  if (text.includes("high")) return "High";
  return "Source";
}

function addSourceRow(rows, seen, source) {
  const title = compactText(source?.title || source?.source_title || source?.url || source?.source_url || "Untitled source", 92);
  const url = compactText(source?.url || source?.source_url || "", 260);
  const key = (url || title).toLowerCase();
  if (!key || seen.has(key)) return;
  seen.add(key);
  rows.push({
    title,
    url,
    domain: domainFromUrl(url) || compactText(source?.domain || source?.source_title || "", 42),
    confidence: confidenceLabel(source?.confidence),
    note: compactText(source?.text || source?.claim || source?.qualification || source?.use_guidance || "", 96),
  });
}

function parseResearchBriefSources(body = "") {
  const rows = [];
  const seen = new Set();
  const lines = String(body || "").split(/\r?\n/);
  for (let index = 0; index < lines.length; index += 1) {
    const titleMatch = lines[index].trim().match(/^\d+\.\s+(.+)$/);
    if (!titleMatch) continue;
    const nextLine = (lines[index + 1] || "").trim();
    const url = /^https?:\/\//i.test(nextLine) ? nextLine : "";
    addSourceRow(rows, seen, { title: titleMatch[1].trim(), url, confidence: "high" });
  }
  return rows;
}

// Real sources the agent captured: run steps -> execution-report digest -> brief.
export function extractResearchSources(assets = [], run = {}) {
  const rows = [];
  const seen = new Set();
  (run.steps || []).forEach((step) => (step.sources || []).forEach((source) => addSourceRow(rows, seen, source)));
  if (rows.length) return rows;

  const digest = getExecutionReport(assets)?.source_digest || {};
  (digest.approved_facts || []).forEach((fact) => addSourceRow(rows, seen, {
    title: fact.source_title, url: fact.source_url, confidence: fact.confidence,
    text: fact.text, qualification: fact.qualification, use_guidance: fact.use_guidance,
  }));
  (digest.objective_claims || []).forEach((claim) => addSourceRow(rows, seen, {
    title: claim.source_title, url: claim.source_url, confidence: "high",
    claim: claim.claim, qualification: claim.qualification,
  }));
  if (rows.length) return rows;

  const brief = assets.find((asset) => asset?.asset_type === "research_brief" && asset.body);
  return parseResearchBriefSources(brief?.body || "");
}

export function extractImagePrompt(assets = []) {
  const promptAsset = assets.find((asset) => asset?.asset_type === "image_prompt" && asset.body);
  return compactText(promptAsset?.body || "", 900);
}

// The headline "real output" text for a stage.
export function getStageFocus(stage = {}, run = {}, assets = []) {
  if (stage.id === "intent") return { label: "Original prompt", text: run.prompt || run.title || "" };
  if (stage.id === "research") return { label: "Smart query", text: stage.query || "" };
  if (stage.id === "visual") return { label: "Generated image prompt", text: extractImagePrompt(assets) };
  return null;
}
